import React, { useState } from 'react';

const RecordingsList = ({ recordings }) => {
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [showModal, setShowModal] = useState(false);

  if (!recordings || recordings.length === 0) {
    return (
      <div className="p-3">
        <p className="text-muted">No recordings found for this user.</p>
      </div>
    );
  }

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    } else if (mins > 0) {
      return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${secs}s`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleViewRecording = (recording) => {
    setSelectedRecording(recording);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRecording(null);
  };

  const handleView = () => {
    if (selectedRecording?.url) {
      window.open(selectedRecording.url, '_blank', 'noopener,noreferrer');
      handleCloseModal();
    }
  };

  const handleCopyLink = async () => {
    if (selectedRecording?.url) {
      try {
        await navigator.clipboard.writeText(selectedRecording.url);
        window.flashMessages?.addMessage({
          type: 'success',
          text: 'Recording URL copied to clipboard!',
        });
        handleCloseModal();
      } catch {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = selectedRecording.url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          window.flashMessages?.addMessage({
            type: 'success',
            text: 'Recording URL copied to clipboard!',
          });
          handleCloseModal();
        } catch {
          window.flashMessages?.addMessage({
            type: 'error',
            text: 'Failed to copy URL. Please copy manually.',
          });
        }
        document.body.removeChild(textArea);
      }
    }
  };

  return (
    <>
      <div className="recordings-list">
        <table className="table table-striped table-hover">
          <thead className="thead-light">
            <tr>
              <th scope="col">ID</th>
              <th scope="col">Active</th>
              <th scope="col">Total</th>
              <th scope="col">Pages</th>
              <th scope="col">Location</th>
              <th scope="col">Device</th>
              <th scope="col">Date</th>
              <th scope="col">Action</th>
            </tr>
          </thead>
          <tbody>
            {recordings.map((recording) => (
              <tr key={recording.id}>
                <td>
                  <code className="small">{recording.id}</code>
                </td>
                <td>{formatDuration(recording.active_duration)}</td>
                <td>{formatDuration(recording.recording_duration)}</td>
                <td>{recording.page_view_count ?? 0}</td>
                <td>{recording.location || 'N/A'}</td>
                <td>{recording.device || 'N/A'}</td>
                <td>{formatDate(recording.start_time)}</td>
                <td>
                  <button
                    type="button"
                    onClick={() => handleViewRecording(recording)}
                    className="btn btn-sm btn-primary"
                  >
                    View Recording
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="p-2">
          <small className="text-muted">
            Total recordings: {recordings.length}
          </small>
        </div>
      </div>

      {/* Modal for recording actions */}
      {showModal && selectedRecording && (
        <div
          className="modal show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={handleCloseModal}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Recording Options</h5>
                <button
                  type="button"
                  className="close"
                  onClick={handleCloseModal}
                  aria-label="Close"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p>
                  <strong>Recording ID:</strong>{' '}
                  <code>{selectedRecording.id}</code>
                </p>
                <p>
                  <strong>URL:</strong>{' '}
                  <small className="text-break">{selectedRecording.url}</small>
                </p>
                <p className="mb-0">Choose an action:</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-info"
                  onClick={handleCopyLink}
                >
                  Copy Link
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleView}
                >
                  View
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RecordingsList;
